import NextAuth from 'next-auth'
import type { Adapter } from 'next-auth/adapters'
import Nodemailer from 'next-auth/providers/nodemailer'
import Google from 'next-auth/providers/google'
import { createTransport } from 'nodemailer'
import { DrizzleAdapter } from '@auth/drizzle-adapter'
import { and, eq } from 'drizzle-orm'
import { cookies } from 'next/headers'
import { db } from '@/db'
import { accounts, sessions, users, verificationTokens, tenantMemberships, tenants, waitlist } from '@/db/schema'
import { recordUserLogin } from '@/lib/auth-user'
import { shouldUseDevMagicLinks, storeDevMagicLink } from '@/lib/dev-magic-link'
import { buildMagicLinkEmail } from '@/lib/magic-link-email'

export const PREFERRED_TENANT_COOKIE = 'preferred-tenant-id'

type DrizzleAdapterSchema = NonNullable<Parameters<typeof DrizzleAdapter<typeof db>>[1]>

const adapterSchema = {
  usersTable: users,
  accountsTable: accounts,
  sessionsTable: sessions,
  verificationTokensTable: verificationTokens,
} as unknown as DrizzleAdapterSchema

const baseAdapter = DrizzleAdapter(db, adapterSchema) as Adapter

// Wraps DrizzleAdapter to inject tenant_id + default role on first sign-in.
// Auth.js cannot write these fields itself since they're not in the AdapterUser shape.
const adapter: Adapter = {
  ...baseAdapter,
  async createUser(data) {
    // Invite guard: only pre-seeded users may sign in. Anyone reaching createUser
    // has no existing row, meaning they were not seeded — add to waitlist and block.
    const email = data.email.toLowerCase().trim()

    // Fire-and-forget waitlist insert (idempotent)
    db.insert(waitlist)
      .values({ email })
      .onConflictDoNothing()
      .catch(() => {})

    throw new Error('NotInvited')
  },
}

export const { handlers, auth, signIn, signOut } = NextAuth({
  adapter,
  session: { strategy: 'database' },
  providers: [
    Google({
      clientId: process.env.GOOGLE_CLIENT_ID,
      clientSecret: process.env.GOOGLE_CLIENT_SECRET,
      allowDangerousEmailAccountLinking: true,
    }),
    Nodemailer({
      server: {
        host: process.env.EMAIL_SERVER_HOST,
        port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
        auth: process.env.EMAIL_SERVER_USER
          ? {
              user: process.env.EMAIL_SERVER_USER,
              pass: process.env.EMAIL_SERVER_PASSWORD,
            }
          : undefined,
      },
      from: process.env.EMAIL_FROM,
      async sendVerificationRequest({ identifier, url, provider, expires }) {
        if (shouldUseDevMagicLinks()) {
          storeDevMagicLink({ email: identifier, url, expires })
          console.log(`\n[auth][dev] Magic link for ${identifier}:\n${url}\n`)
          return
        }

        const transport = createTransport(provider.server)
        const { host } = new URL(url)
        const { subject, html, text } = buildMagicLinkEmail(url, host)
        const result = await transport.sendMail({
          to: identifier,
          from: provider.from,
          subject,
          text,
          html,
        })

        const failed = [...(result.rejected ?? []), ...(result.pending ?? [])].filter(Boolean)
        if (failed.length) throw new Error(`Email (${failed.join(', ')}) could not be sent`)
      },
    }),
  ],
  callbacks: {
    async session({ session, user }) {
      const memberships = await db
        .select({
          tenantId: tenantMemberships.tenantId,
          tenantName: tenants.name,
          role: tenantMemberships.role,
        })
        .from(tenantMemberships)
        .innerJoin(tenants, eq(tenants.id, tenantMemberships.tenantId))
        .where(
          and(
            eq(tenantMemberships.userId, user.id),
            eq(tenantMemberships.isActive, true),
          )
        )

      session.user.id = user.id
      session.user.hasMultipleTenants = memberships.length > 1

      if (memberships.length === 0) {
        session.user.tenantId = ''
        session.user.tenantName = ''
        session.user.role = 'line_manager'
        return session
      }

      // Prefer the tenant stored in the cookie if the user still has an active membership there
      let active = memberships[0]
      if (memberships.length > 1) {
        const cookieStore = await cookies()
        const preferred = cookieStore.get(PREFERRED_TENANT_COOKIE)?.value
        if (preferred) {
          const match = memberships.find((m) => m.tenantId === preferred)
          if (match) active = match
        }
      }

      session.user.tenantId = active.tenantId
      session.user.tenantName = active.tenantName
      session.user.role = active.role
      return session
    },
  },
  events: {
    async signIn({ user, account }) {
      if (user.id) {
        await recordUserLogin(user.id, account?.provider)
      }
    },
  },
  pages: {
    signIn: '/login',
    verifyRequest: '/login/verify',
    error: '/login',
  },
})
